<section class="beneficiary-section">
  <!-- Header: "Enter beneficiaries" + Help link -->
  <div class="beneficiary-section__header">
    <h2 class="c11n-text-xl-headline">{{ content.BENEFICIARY_SECTION_HEADER }}</h2>
    <a
      c11n-link
      [iconName]="'help'"
      [iconPosition]="'trailing'"
      [variant]="'secondary-reinforced'"
      (click)="handleHelpClick()"
      aria-label="Open beneficiaries help"
      >{{ content.BENEFICIARY_HELP_LINK_TEXT }}</a
    >
  </div>

  <!-- Disclaimer -->
  <p class="c11n-text-md beneficiary-section__disclaimer">
    {{ content.BENEFICIARY_SECTION_DISCLAIMER }}
  </p>

  <!-- INITIAL STATE: No benes saved yet — show input card directly -->
  <ng-container *ngIf="!hasBeneficiaries">
    <ng-container *ngFor="let reset of [inputCardResetKey]">
      <app-input-beneficiary-card
        *ngIf="addingSection === 'PRIMARY'"
        [mode]="'add'"
        [skipTypeSelection]="true"
        [householdMembers]="householdMembers"
        [householdPeople]="householdPeople"
        [trusts]="trusts"
        [settlors]="settlors"
        [section]="'PRIMARY'"
        [isSaving]="isSaving"
        [showSelectError]="showIncompleteError"
        (save)="handleSave($event, 'PRIMARY')"
        (cancel)="handleCancelInput()"
      ></app-input-beneficiary-card>
    </ng-container>

    <!-- Household load error -->
    <c11n-hint-error
      *ngIf="showHouseholdLoadError"
      [hasError]="true"
      [size]="'medium'"
      [errorText]="content.BENEFICIARY_HOUSEHOLD_LOAD_ERROR"
      hintErrorId="bene-household-load-error"
    ></c11n-hint-error>

    <!-- Required error (shown on submit when POD checked but no benes) -->
    <c11n-hint-error
      *ngIf="showRequiredError"
      [hasError]="true"
      [size]="'medium'"
      [errorText]="content.BENEFICIARY_REQUIRED_ERROR"
      hintErrorId="bene-required-error"
    ></c11n-hint-error>
  </ng-container>

  <!-- POPULATED STATE: At least one bene saved — show after household loads so c11n-dropdown gets menuData at init -->
  <ng-container *ngIf="hasBeneficiaries && householdLoaded">
    <div class="beneficiary-section__groups-container">
      <!-- PRIMARY BENEFICIARIES -->
      <section class="beneficiary-section__group">
        <h3 class="c11n-text-lg-bold">{{ content.BENEFICIARY_PRIMARY_HEADER }}</h3>

        <ng-container
          *ngFor="
            let bene of beneficiaryState.primaryBeneficiaries;
            let i = index;
            trackBy: trackByBeneficiary
          "
        >
          <app-beneficiary-card
            *ngIf="editingBeneficiary !== bene"
            [beneficiary]="bene"
            [section]="'PRIMARY'"
            [householdMembers]="householdMembers"
            [householdPeople]="householdPeople"
            [trusts]="trusts"
            (edit)="handleEdit($event, 'PRIMARY')"
            (remove)="handleRemove($event, 'PRIMARY')"
            (distributionChange)="handleDistributionChange($event, 'PRIMARY')"
            (personChange)="handlePersonChange($event, 'PRIMARY')"
            (trustChange)="handleTrustChange($event, 'PRIMARY')"
          ></app-beneficiary-card>

          <ng-container *ngIf="editingBeneficiary === bene && editingSection === 'PRIMARY'">
            <app-input-beneficiary-card
              [mode]="'edit'"
              [existingData]="bene"
              [householdMembers]="householdMembers"
              [householdPeople]="householdPeople"
              [trusts]="trusts"
              [settlors]="settlors"
              [section]="'PRIMARY'"
              [isSaving]="isSaving"
              (save)="handleSave($event, 'PRIMARY')"
              (saveEdit)="handleSaveEdit($event, 'PRIMARY')"
              (saveEditTrust)="handleSaveEditTrust($event, 'PRIMARY')"
              (cancel)="handleCancelInput()"
            ></app-input-beneficiary-card>

            <c11n-hint-error
              *ngIf="showIncompleteError"
              [hasError]="true"
              [size]="'medium'"
              [errorText]="content.BENEFICIARY_INCOMPLETE_ERROR"
              hintErrorId="bene-incomplete-error"
            ></c11n-hint-error>
          </ng-container>
        </ng-container>

        <ng-container *ngIf="addingSection === 'PRIMARY'">
          <app-input-beneficiary-card
            [mode]="'add'"
            [householdMembers]="householdMembers"
            [householdPeople]="householdPeople"
            [trusts]="trusts"
            [settlors]="settlors"
            [section]="'PRIMARY'"
            [showCancelOnTypeSelection]="true"
            [isSaving]="isSaving"
            (save)="handleSave($event, 'PRIMARY')"
            (cancel)="handleCancelInput()"
          ></app-input-beneficiary-card>

          <c11n-hint-error
            *ngIf="showIncompleteError"
            [hasError]="true"
            [size]="'medium'"
            [errorText]="content.BENEFICIARY_INCOMPLETE_ERROR"
            hintErrorId="bene-incomplete-error"
          ></c11n-hint-error>
        </ng-container>

        <div class="beneficiary-section__button-total-row">
          <c11n-button
            [variant]="'secondary'"
            [size]="'small'"
            [iconName]="'plus'"
            [iconPosition]="'leading'"
            [labelText]="content.BENEFICIARY_ADD_PRIMARY"
            (clickEvent)="handleAdd('PRIMARY')"
          >
          </c11n-button>

          <div
            class="beneficiary-section__distribution-tracker"
            *ngIf="beneficiaryState.primaryBeneficiaries.length > 0"
          >
            <c11n-hint-error
              *ngIf="showPrimaryDistributionError"
              [hasError]="true"
              [size]="'medium'"
              [errorText]="content.BENEFICIARY_DISTRIBUTION_ERROR"
              hintErrorId="primary-dist-error"
            ></c11n-hint-error>
            <div class="beneficiary-section__total">
              <span class="c11n-text-sm">{{ content.BENEFICIARY_PRIMARY_TOTAL_LABEL }}</span>
              <span class="c11n-text-sm-bold">{{ primaryTotal }}%</span>
              <c11n-icon
                *ngIf="primaryTotal === 100"
                [name]="'success'"
                [size]="'small'"
                [altText]="'Distribution total is 100%'"
                class="beneficiary-section__check-icon"
              ></c11n-icon>
            </div>
          </div>
        </div>
      </section>

      <!-- BACKUP BENEFICIARIES — only visible when at least one primary exists or backups already saved -->
      <section
        class="beneficiary-section__group"
        *ngIf="
          beneficiaryState.primaryBeneficiaries.length > 0 ||
          beneficiaryState.backupBeneficiaries.length > 0
        "
      >
        <h3 class="c11n-text-lg-bold">{{ content.BENEFICIARY_BACKUP_HEADER }}</h3>

        <ng-container
          *ngFor="
            let bene of beneficiaryState.backupBeneficiaries;
            let i = index;
            trackBy: trackByBeneficiary
          "
        >
          <app-beneficiary-card
            *ngIf="editingBeneficiary !== bene"
            [beneficiary]="bene"
            [section]="'CONTINGENT'"
            [householdMembers]="householdMembers"
            [householdPeople]="householdPeople"
            [trusts]="trusts"
            (edit)="handleEdit($event, 'CONTINGENT')"
            (remove)="handleRemove($event, 'CONTINGENT')"
            (distributionChange)="handleDistributionChange($event, 'CONTINGENT')"
            (personChange)="handlePersonChange($event, 'CONTINGENT')"
            (trustChange)="handleTrustChange($event, 'CONTINGENT')"
          ></app-beneficiary-card>

          <ng-container *ngIf="editingBeneficiary === bene && editingSection === 'CONTINGENT'">
            <app-input-beneficiary-card
              [mode]="'edit'"
              [existingData]="bene"
              [householdMembers]="householdMembers"
              [householdPeople]="householdPeople"
              [trusts]="trusts"
              [settlors]="settlors"
              [section]="'CONTINGENT'"
              [isSaving]="isSaving"
              (save)="handleSave($event, 'CONTINGENT')"
              (saveEdit)="handleSaveEdit($event, 'CONTINGENT')"
              (saveEditTrust)="handleSaveEditTrust($event, 'CONTINGENT')"
              (cancel)="handleCancelInput()"
            ></app-input-beneficiary-card>

            <c11n-hint-error
              *ngIf="showIncompleteError"
              [hasError]="true"
              [size]="'medium'"
              [errorText]="content.BENEFICIARY_INCOMPLETE_ERROR"
              hintErrorId="bene-incomplete-error"
            ></c11n-hint-error>
          </ng-container>
        </ng-container>

        <ng-container *ngIf="addingSection === 'CONTINGENT'">
          <app-input-beneficiary-card
            [mode]="'add'"
            [householdMembers]="householdMembers"
            [householdPeople]="householdPeople"
            [trusts]="trusts"
            [settlors]="settlors"
            [section]="'CONTINGENT'"
            [showCancelOnTypeSelection]="true"
            [isSaving]="isSaving"
            (save)="handleSave($event, 'CONTINGENT')"
            (cancel)="handleCancelInput()"
          ></app-input-beneficiary-card>

          <c11n-hint-error
            *ngIf="showIncompleteError"
            [hasError]="true"
            [size]="'medium'"
            [errorText]="content.BENEFICIARY_INCOMPLETE_ERROR"
            hintErrorId="bene-incomplete-error"
          ></c11n-hint-error>
        </ng-container>

        <div class="beneficiary-section__button-total-row">
          <c11n-button
            [variant]="'secondary'"
            [size]="'small'"
            [iconName]="'plus'"
            [iconPosition]="'leading'"
            [labelText]="content.BENEFICIARY_ADD_BACKUP"
            (clickEvent)="handleAdd('CONTINGENT')"
          >
          </c11n-button>

          <div
            class="beneficiary-section__distribution-tracker"
            *ngIf="beneficiaryState.backupBeneficiaries.length > 0"
          >
            <c11n-hint-error
              *ngIf="showBackupDistributionError"
              [hasError]="true"
              [size]="'medium'"
              [errorText]="content.BENEFICIARY_DISTRIBUTION_ERROR"
              hintErrorId="backup-dist-error"
            ></c11n-hint-error>
            <div class="beneficiary-section__total">
              <span class="c11n-text-sm">{{ content.BENEFICIARY_BACKUP_TOTAL_LABEL }}</span>
              <span class="c11n-text-sm-bold">{{ backupTotal }}%</span>
              <c11n-icon
                *ngIf="backupTotal === 100"
                [name]="'success'"
                [size]="'small'"
                [altText]="'Distribution total is 100%'"
                class="beneficiary-section__check-icon"
              ></c11n-icon>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Required error -->
    <c11n-hint-error
      *ngIf="showRequiredError"
      [hasError]="true"
      [size]="'medium'"
      [errorText]="content.BENEFICIARY_REQUIRED_ERROR"
      hintErrorId="bene-required-error"
    ></c11n-hint-error>

    <!-- Zero distribution error (MEZ API requires minimum 1% per beneficiary) -->
    <c11n-hint-error
      *ngIf="showZeroDistributionError"
      [hasError]="true"
      [size]="'medium'"
      [errorText]="content.BENEFICIARY_ZERO_DISTRIBUTION_ERROR"
      hintErrorId="bene-zero-distribution-error"
    ></c11n-hint-error>
  </ng-container>

  <!-- Duplicate person in same section error -->
  <c11n-hint-error
    *ngIf="showDuplicatePersonError"
    [hasError]="true"
    [size]="'medium'"
    [errorText]="content.BENEFICIARY_DUPLICATE_PERSON_ERROR"
    hintErrorId="bene-duplicate-person-error"
  ></c11n-hint-error>

  <!-- ZOK create person failed error -->
  <c11n-hint-error
    *ngIf="showCreatePersonError"
    [hasError]="true"
    [size]="'medium'"
    [errorText]="content.BENEFICIARY_CREATE_PERSON_ERROR"
    hintErrorId="bene-create-person-error"
  ></c11n-hint-error>

  <!-- ROB create trust failed error -->
  <c11n-hint-error
    *ngIf="showCreateTrustError"
    [hasError]="true"
    [size]="'medium'"
    [errorText]="content.BENEFICIARY_TRUST_CREATE_ERROR"
    hintErrorId="bene-create-trust-error"
  ></c11n-hint-error>

  <!-- Remove Beneficiary Modal -->
  <c11n-modal-dialog
    [headingText]="content.BENEFICIARY_REMOVE_HEADER"
    [primaryButtonLabel]="content.BENEFICIARY_REMOVE_CONFIRM"
    [secondaryButtonLabel]="content.BENEFICIARY_REMOVE_CANCEL"
    (primaryClick)="confirmRemove()"
    (secondaryClick)="cancelRemove()"
    (closeEvent)="cancelRemove()"
    #removeModal
  >
    <div c11n-modal-dialog-body>
      <p class="c11n-text-md">{{ content.BENEFICIARY_REMOVE_MESSAGE }}</p>
    </div>
  </c11n-modal-dialog>

  <!-- Duplicate Detection Modal -->
  <c11n-modal-dialog
    [headingText]="content.BENEFICIARY_DUPLICATE_HEADER"
    [primaryButtonLabel]="content.BENEFICIARY_DUPLICATE_ADD_ANYWAY"
    [secondaryButtonLabel]="content.BENEFICIARY_DUPLICATE_DISCARD"
    (primaryClick)="confirmAddDuplicate()"
    (secondaryClick)="discardDuplicate()"
    (closeEvent)="discardDuplicate()"
    #duplicateModal
  >
    <div c11n-modal-dialog-body>
      <p class="c11n-text-md">{{ duplicateDetectionMessage }}</p>
      <p class="c11n-text-md beneficiary-section__modal-paragraph">
        {{ content.BENEFICIARY_DUPLICATE_DISCARD_MESSAGE }}
      </p>
      <p class="c11n-text-md beneficiary-section__modal-paragraph">
        {{ content.BENEFICIARY_DUPLICATE_ADD_ANYWAY_MESSAGE }}
      </p>
    </div>
  </c11n-modal-dialog>
</section>
